import path from 'path';
import { Logger } from './logger';
import {
  appRootDir,
  checkDir,
  readFile,
  readFiles,
  writeFile,
} from '~/providers/FileProvider';
import { tryParse } from './tryparse';

class CacheNode {
  key: string;
  value: any;
  next: any;
  prev: any;
  constructor(key: string, value: any) {
    this.key = key;
    this.value = value;
    this.next = null;
    this.prev = null;
  }
}

class LruCache {
  map: { [key: string]: any };
  head: any;
  tail: any;
  limit: number;
  size: number;
  constructor(limit = 100) {
    this.map = {};
    this.head = null;
    this.tail = null;
    this.limit = limit || 100;
    this.size = 0;
  }

  get(key: string) {
    if (this.map[key]) {
      const value = this.map[key].value;
      const node = new CacheNode(key, value);
      this.remove(key);
      this.setHead(node);
      return value;
    }
  }

  set(key: string, value: any) {
    const node = new CacheNode(key, value);
    if (this.map[key]) {
      this.remove(key);
    } else {
      if (this.size >= this.limit) {
        delete this.map[this.tail.key];
        this.size--;
        this.tail = this.tail.prev;
        this.tail.next = null;
      }
    }
    this.setHead(node);
  }

  setHead(node: CacheNode) {
    node.next = this.head;
    node.prev = null;
    if (this.head) {
      this.head.prev = node;
    }
    this.head = node;
    if (!this.tail) {
      this.tail = node;
    }
    this.size++;
    this.map[node.key] = node;
  }

  remove(key: string) {
    const node = this.map[key];
    if (node.prev) {
      node.prev.next = node.next;
    } else {
      this.head = node.next;
    }
    if (node.next) {
      node.next.prev = node.prev;
    } else {
      this.tail = node.prev;
    }
    delete this.map[key];
    this.size--;
  }
}

export class CachedFetch {
  cache = new LruCache();
  cacheDir: string;
  fileCache?: string[];

  constructor(alias: string, projectId: string) {
    // initialise file cache
    this.fileCache =
      process.env.RECORD_MOCKS || process.env.NODE_ENV === 'test'
        ? undefined
        : [];
    this.cacheDir = path.join(appRootDir, `cache/${alias}/${projectId}`);

    if (this.fileCache) {
      // load file cache
      checkDir(this.cacheDir);
      this.fileCache = readFiles(this.cacheDir);
    }
  }

  hasFile = (key: string) => this.fileCache?.find((f) => f === key);
  getFile = (key: string) => tryParse(readFile(path.join(this.cacheDir, key)));
  writeFile = (key: string, value: any) => {
    if (!this.fileCache) return; // Avoid writing if we're not using the cache at all
    Logger.debug(`[cache] Writing file ${key}`);
    const filePath = path.join(this.cacheDir, key);
    checkDir(filePath);
    writeFile(filePath, JSON.stringify(value));
  };

  request<T>(
    key: string,
    execute: () => Promise<T>,
    noCache: boolean
  ): Promise<T> {
    if (!noCache && this.hasFile(key)) {
      Logger.debug(`[cache] File hit ${key}`);
      const filePayload = this.getFile(key);
      if (filePayload !== false) return this.getFile(key);
    }
    if (!this.cache.get(key)) {
      const promise = execute();
      this.cache.set(key, promise);
      promise
        .then((value) => {
          if (this.fileCache)
            try {
              this.writeFile(key, value);
            } catch (ex) {
              Logger.warning(`[cache] Failed to write file for ${key}\n${ex}`);
            }
        })
        .catch(() => {
          this.cache.remove(key);
        });
    } else Logger.standard(`[cache] Request hit ${key}`);
    return this.cache.get(key);
  }
}
